import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, Minus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdvancesPage() {
  const [advances, setAdvances] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deductDialogOpen, setDeductDialogOpen] = useState(false);
  const [selectedAdvance, setSelectedAdvance] = useState(null);
  const [deductAmount, setDeductAmount] = useState(0);
  const { t } = useLanguage();
  const { isHR, isAdmin } = useAuth();
  
  const [form, setForm] = useState({
    employee_id: '',
    amount: 0,
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [advancesRes, employeesRes] = await Promise.all([
          axios.get(`${API}/advances`),
          axios.get(`${API}/employees`, { params: { is_active: true } })
        ]);
        setAdvances(advancesRes.data);
        setEmployees(employeesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error(t('errorOccurred'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [t]);

  const refetchData = async () => {
    try {
      const [advancesRes, employeesRes] = await Promise.all([
        axios.get(`${API}/advances`),
        axios.get(`${API}/employees`, { params: { is_active: true } })
      ]);
      setAdvances(advancesRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('errorOccurred'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/advances`, {
        ...form,
        amount: parseFloat(form.amount)
      });
      toast.success(t('savedSuccessfully'));
      setDialogOpen(false);
      setForm({ employee_id: '', amount: 0, date: new Date().toISOString().split('T')[0], notes: '' });
      refetchData();
    } catch (error) {
      console.error('Error creating advance:', error);
      toast.error(t('errorOccurred'));
    }
  };

  const handleDeduct = async () => {
    try {
      await axios.put(`${API}/advances/${selectedAdvance.id}/deduct`, { amount: parseFloat(deductAmount) });
      toast.success(t('savedSuccessfully'));
      setDeductDialogOpen(false);
      setSelectedAdvance(null);
      setDeductAmount(0);
      refetchData();
    } catch (error) {
      console.error('Error deducting advance:', error);
      const detail = error.response?.data?.detail;
      let errorMessage = t('errorOccurred');
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
      }
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (advanceId) => {
    if (!window.confirm(t('confirmDeleteAdvance'))) return;
    
    try {
      await axios.delete(`${API}/advances/${advanceId}`);
      toast.success(t('deletedSuccessfully'));
      refetchData();
    } catch (error) {
      console.error('Error deleting advance:', error);
      toast.error(t('errorOccurred'));
    }
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    return emp ? `${emp.first_name} ${emp.last_name}` : empId;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    }).format(value || 0) + ' TND';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#E31E24] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="advances-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold text-[#1A1A1A]">{t('advances')}</h1>
        {isHR() && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#E31E24] hover:bg-[#C41218] text-[#1A1A1A] btn-primary-glow" data-testid="add-advance-btn">
                <Plus className="w-4 h-4 mr-2" />
                {t('newAdvance')}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200">
              <DialogHeader>
                <DialogTitle className="text-[#1A1A1A]">{t('newAdvance')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-gray-500">{t('employees')}</Label>
                  <Select value={form.employee_id} onValueChange={(v) => setForm(prev => ({ ...prev, employee_id: v }))}>
                    <SelectTrigger className="bg-white border-gray-200 text-[#1A1A1A]">
                      <SelectValue placeholder={t('employees')} />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.first_name} {emp.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500">{t('amount')}</Label>
                  <Input
                    type="number"
                    step="0.001"
                    value={form.amount}
                    onChange={(e) => setForm(prev => ({ ...prev, amount: e.target.value }))}
                    className="bg-white border-gray-200 text-[#1A1A1A]"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500">{t('date')}</Label>
                  <Input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value }))}
                    className="bg-white border-gray-200 text-[#1A1A1A]"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500">{t('notes')}</Label>
                  <Input
                    value={form.notes}
                    onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                    className="bg-white border-gray-200 text-[#1A1A1A]"
                  />
                </div>
                <Button type="submit" className="w-full bg-[#E31E24] hover:bg-[#C41218] text-[#1A1A1A]">
                  {t('save')}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Table */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-200 hover:bg-transparent">
                  <TableHead className="table-header">{t('employees')}</TableHead>
                  <TableHead className="table-header">{t('date')}</TableHead>
                  <TableHead className="table-header">{t('amount')}</TableHead>
                  <TableHead className="table-header">{t('remainingAmount')}</TableHead>
                  <TableHead className="table-header">{t('status')}</TableHead>
                  <TableHead className="table-header text-right">{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {advances.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-gray-400">
                      {t('noData')}
                    </TableCell>
                  </TableRow>
                ) : (
                  advances.map((adv) => (
                    <TableRow key={adv.id} className="table-row" data-testid={`advance-row-${adv.id}`}>
                      <TableCell className="text-[#1A1A1A]">{getEmployeeName(adv.employee_id)}</TableCell>
                      <TableCell className="text-gray-500 font-mono">{adv.date}</TableCell>
                      <TableCell className="font-mono text-[#1A1A1A]">{formatCurrency(adv.amount)}</TableCell>
                      <TableCell className="font-mono text-amber-400">{formatCurrency(adv.remaining_amount)}</TableCell>
                      <TableCell>
                        {adv.status === 'closed' ? (
                          <Badge className="badge-success">{t('closed')}</Badge>
                        ) : (
                          <Badge className="badge-warning">{t('open')}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {adv.status === 'open' && isHR() && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedAdvance(adv);
                                setDeductAmount(adv.remaining_amount);
                                setDeductDialogOpen(true);
                              }}
                              className="text-amber-400 hover:text-amber-300"
                              data-testid={`deduct-advance-${adv.id}`}
                            >
                              <Minus className="w-4 h-4 mr-1" />
                              {t('deduct')}
                            </Button>
                          )}
                          {isAdmin() && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(adv.id)}
                              className="text-gray-500 hover:text-[#E31E24]"
                              data-testid={`delete-advance-${adv.id}`}
                              title={t('deleteOnlyAdmin')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Deduct Dialog */}
      <Dialog open={deductDialogOpen} onOpenChange={setDeductDialogOpen}>
        <DialogContent className="bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-[#1A1A1A]">{t('deduct')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-500">
              {t('remainingAmount')}: {formatCurrency(selectedAdvance?.remaining_amount)}
            </p>
            <div className="space-y-2">
              <Label className="text-gray-500">{t('amount')}</Label>
              <Input
                type="number"
                step="0.001"
                max={selectedAdvance?.remaining_amount}
                value={deductAmount}
                onChange={(e) => setDeductAmount(e.target.value)}
                className="bg-white border-gray-200 text-[#1A1A1A]"
              />
            </div>
            <Button onClick={handleDeduct} className="w-full bg-[#E31E24] hover:bg-[#C41218] text-[#1A1A1A]">
              {t('confirm')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
